import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { SERIES_COLORS, valueTone } from './theme';
import { formatHour } from './format';

const PLOT_TOP = 12;
const AXIS_HEIGHT = 20;
const SIDE = 2;

/** Monotone cubic (Fritsch-Carlson): smooth, but never overshoots between points. */
const monotonePath = (points) => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M${points[0].x},${points[0].y}`;
  const n = points.length;
  const slopes = [];
  for (let i = 0; i < n - 1; i += 1) {
    const dx = points[i + 1].x - points[i].x;
    slopes.push(dx === 0 ? 0 : (points[i + 1].y - points[i].y) / dx);
  }
  const tangents = points.map((_, i) => {
    if (i === 0) return slopes[0];
    if (i === n - 1) return slopes[n - 2];
    return slopes[i - 1] * slopes[i] <= 0 ? 0 : (slopes[i - 1] + slopes[i]) / 2;
  });
  for (let i = 0; i < n - 1; i += 1) {
    if (slopes[i] === 0) {
      tangents[i] = 0;
      tangents[i + 1] = 0;
    } else {
      const a = tangents[i] / slopes[i];
      const b = tangents[i + 1] / slopes[i];
      const h = a * a + b * b;
      if (h > 9) {
        const t = 3 / Math.sqrt(h);
        tangents[i] = t * a * slopes[i];
        tangents[i + 1] = t * b * slopes[i];
      }
    }
  }
  let path = `M${points[0].x},${points[0].y}`;
  for (let i = 0; i < n - 1; i += 1) {
    const third = (points[i + 1].x - points[i].x) / 3;
    path += `C${points[i].x + third},${points[i].y + tangents[i] * third},${points[i + 1].x - third},${points[i + 1].y - tangents[i + 1] * third},${points[i + 1].x},${points[i + 1].y}`;
  }
  return path;
};

/**
 * Cumulative GGR through the period (total, casino, sport). Touch/drag, hover or the arrow keys
 * show the figures for any hour or day.
 */
export default function CumulativeGgrChart({
  series,
  buckets,
  bucketType,
  nowIndex,
  isLive,
  formatValue,
  formatSignedValue,
  formatBucketLabel,
  labels
}) {
  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const [width, setWidth] = useState(0);
  const [activeIndex, setActiveIndex] = useState(null);
  const gradientId = `ggr-area-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const update = () => setWidth(element.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setActiveIndex(null);
  }, [buckets]);

  // A live period's axis stops at "now" so the lines always use the full width
  const count = isLive ? Math.min(Math.max(nowIndex + 1, 1), buckets.length) : buckets.length;
  const lastIndex = Math.min(count, series.length) - 1;
  const shown = useMemo(() => series.slice(0, lastIndex + 1), [series, lastIndex]);
  const hasData = shown.some((point) => point.total !== 0 || point.casino !== 0 || point.sport !== 0);

  const [min, max] = useMemo(() => {
    const values = [0];
    shown.forEach((point) => values.push(point.cumTotal, point.cumCasino, point.cumSport));
    const low = Math.min(...values);
    const high = Math.max(...values);
    const headroom = (high - low) * 0.08;
    return high > low ? [low < 0 ? low - headroom : low, high + headroom] : [low, low + 1];
  }, [shown]);

  const height = width >= 640 ? 220 : 180;
  const plotHeight = height - PLOT_TOP - AXIS_HEIGHT;
  const innerWidth = Math.max(width - SIDE * 2, 1);
  const xAt = (boundary) => SIDE + (boundary / count) * innerWidth;
  const y = (value) => PLOT_TOP + ((max - value) / (max - min)) * plotHeight;
  const cumulativePath = (key) =>
    monotonePath([{ x: xAt(0), y: y(0) }, ...shown.map((point, i) => ({ x: xAt(i + 1), y: y(point[key]) }))]);

  const finalTotal = shown.length ? shown[shown.length - 1].cumTotal : 0;
  const totalColor = finalTotal < 0 ? SERIES_COLORS.totalNegative : SERIES_COLORS.total;
  const totalPath = cumulativePath('cumTotal');
  const areaPath = shown.length ? `${totalPath}L${xAt(shown.length)},${y(0)}Z` : '';

  const axisLabels = useMemo(() => {
    if (bucketType === 'hour') {
      const step = width >= 640 ? 3 : 6;
      const boundaries = [];
      for (let k = 0; k < count; k += step) boundaries.push(k);
      if (boundaries.length > 1 && count - boundaries[boundaries.length - 1] < step / 2) boundaries.pop();
      boundaries.push(count);
      return boundaries.map((k) => ({
        key: `h${k}`,
        x: k,
        text: k < count ? formatHour(buckets[k].hour) : isLive ? labels.now : '24:00',
        anchor: k === 0 ? 'start' : k === count ? 'end' : 'middle'
      }));
    }
    return buckets.slice(0, count)
      .map((bucket, i) => ({ i, day: Number(bucket.day.slice(8)) }))
      .filter(({ day }) => count <= 7 || day === 1 || day % 5 === 0)
      .map(({ i, day }) => ({ key: `d${i}`, x: i + 0.5, text: String(day), anchor: 'middle' }));
  }, [bucketType, buckets, count, width, isLive, labels.now]);

  const indexFromClientX = (clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left - SIDE;
    return Math.max(0, Math.min(count - 1, Math.floor((x / innerWidth) * count)));
  };

  const handlers = {
    onPointerDown: (event) => {
      draggingRef.current = true;
      setActiveIndex(indexFromClientX(event.clientX));
    },
    onPointerMove: (event) => {
      if (event.pointerType === 'mouse' || draggingRef.current) setActiveIndex(indexFromClientX(event.clientX));
    },
    onPointerUp: () => {
      draggingRef.current = false;
    },
    onPointerCancel: () => {
      draggingRef.current = false;
    },
    onPointerLeave: (event) => {
      draggingRef.current = false;
      if (event.pointerType === 'mouse') setActiveIndex(null);
    },
    onKeyDown: (event) => {
      const moves = { ArrowRight: 1, ArrowLeft: -1 };
      if (moves[event.key]) {
        event.preventDefault();
        setActiveIndex((index) => Math.max(0, Math.min(count - 1, index === null ? lastIndex : index + moves[event.key])));
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        setActiveIndex(event.key === 'Home' ? 0 : count - 1);
      } else if (event.key === 'Escape') {
        setActiveIndex(null);
      }
    }
  };

  const activePoint = activeIndex !== null && activeIndex <= lastIndex ? series[activeIndex] : null;
  const activeX = activeIndex !== null ? xAt(activeIndex + 1) : 0;
  const tooltipPosition = activeX < width * 0.3
    ? { left: Math.max(activeX - 12, 0) }
    : activeX > width * 0.7
      ? { right: Math.max(width - activeX - 12, 0) }
      : { left: activeX, transform: 'translateX(-50%)' };

  return (
    <div className="relative w-full" dir="ltr">
      <div
        ref={containerRef}
        role="img"
        aria-label={labels.summary}
        tabIndex={0}
        className="relative w-full cursor-crosshair select-none rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
        style={{ height, touchAction: 'pan-y' }}
        {...handlers}
      >
        {width > 0 && (
          <svg width={width} height={height} className="block overflow-visible">
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={totalColor} stopOpacity="0.2" />
                <stop offset="100%" stopColor={totalColor} stopOpacity="0" />
              </linearGradient>
            </defs>

            <line x1={SIDE} x2={width - SIDE} y1={y(0)} y2={y(0)} strokeWidth="1" className="stroke-gray-200 dark:stroke-gray-700" />

            {areaPath && <path d={areaPath} fill={`url(#${gradientId})`} />}
            <path d={cumulativePath('cumCasino')} fill="none" stroke={SERIES_COLORS.casino} strokeWidth="1.5" strokeLinejoin="round" opacity="0.8" />
            <path d={cumulativePath('cumSport')} fill="none" stroke={SERIES_COLORS.sport} strokeWidth="1.5" strokeLinejoin="round" opacity="0.8" />
            <path d={totalPath} fill="none" stroke={totalColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

            {isLive && shown.length > 0 && activeIndex === null && (
              <circle cx={xAt(shown.length)} cy={y(finalTotal)} r="3.5" fill={totalColor} />
            )}

            {activeIndex !== null && (
              <g>
                <line
                  x1={activeX}
                  x2={activeX}
                  y1={PLOT_TOP}
                  y2={PLOT_TOP + plotHeight}
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  className="stroke-gray-300 dark:stroke-gray-600"
                />
                {activePoint && (
                  <>
                    <circle cx={activeX} cy={y(activePoint.cumCasino)} r="3" fill="white" stroke={SERIES_COLORS.casino} strokeWidth="1.5" />
                    <circle cx={activeX} cy={y(activePoint.cumSport)} r="3" fill="white" stroke={SERIES_COLORS.sport} strokeWidth="1.5" />
                    <circle cx={activeX} cy={y(activePoint.cumTotal)} r="4.5" fill="white" stroke={totalColor} strokeWidth="2.5" />
                  </>
                )}
              </g>
            )}

            {axisLabels.map((label) => (
              <text
                key={label.key}
                x={xAt(label.x)}
                y={height - 4}
                textAnchor={label.anchor}
                className="fill-gray-400 dark:fill-gray-500"
                style={{ fontSize: 10 }}
              >
                {label.text}
              </text>
            ))}
          </svg>
        )}

        {!hasData && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center pb-5 text-sm text-gray-400 dark:text-gray-500">
            {labels.empty}
          </div>
        )}
      </div>

      {activeIndex !== null && (
        <div
          aria-live="polite"
          className="pointer-events-none absolute top-0 z-10 whitespace-nowrap rounded-lg bg-white/95 px-2.5 py-1.5 text-[11px] shadow-sm ring-1 ring-gray-200 dark:bg-gray-900/95 dark:ring-gray-700"
          style={tooltipPosition}
        >
          <p className="text-gray-500 dark:text-gray-400">{formatBucketLabel(buckets[activeIndex])}</p>
          <p className={`text-sm font-semibold tabular-nums ${activePoint ? valueTone(activePoint.cumTotal) : 'text-gray-900 dark:text-white'}`}>
            {activePoint ? formatValue(activePoint.cumTotal) : '—'}
          </p>
          {activePoint && (
            <>
              <p className="flex items-center gap-1.5 tabular-nums text-gray-600 dark:text-gray-300">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: SERIES_COLORS.casino }} aria-hidden="true" />
                <span className="sr-only">{labels.casino}</span>
                {formatValue(activePoint.cumCasino)}
                <span className="ms-1 h-1.5 w-1.5 rounded-full" style={{ backgroundColor: SERIES_COLORS.sport }} aria-hidden="true" />
                <span className="sr-only">{labels.sport}</span>
                {formatValue(activePoint.cumSport)}
              </p>
              <p className="tabular-nums text-gray-400 dark:text-gray-500">
                {formatSignedValue(activePoint.total)} {labels.bucketTotal}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
